class SearchTypingEffect {
  constructor(element, options = {}) {
    this.element = element;
    this.phrases = options.phrases || ['100% whey protein'];
    this.typeSpeed = options.typeSpeed || 50;
    this.deleteSpeed = options.deleteSpeed || 30;
    this.pauseDuration = options.pauseDuration || 2000;
    this.startDelay = options.startDelay || 500;
    
    this.currentPhraseIndex = 0;
    this.currentCharIndex = 0;
    this.isDeleting = false;
    this.isTyping = false;
    this.timeoutId = null;
    
    this.originalPlaceholder = element.placeholder;
    this.init();
  }
  
  init() {
    // Store original attributes
    this.originalValue = this.element.value;
    
    // Add event listeners
    this.element.addEventListener('focus', this.handleFocus.bind(this));
    this.element.addEventListener('blur', this.handleBlur.bind(this));
    this.element.addEventListener('input', this.handleInput.bind(this));
    
    // Start typing effect if element is not focused
    if (document.activeElement !== this.element && !this.element.value) {
      setTimeout(() => this.startTyping(), this.startDelay);
    }
  }
  
  startTyping() {
    if (this.isTyping || this.element.value) return;
    
    this.isTyping = true;
    this.typePhrase();
  }
  
  stopTyping() {
    this.isTyping = false;
    
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    
    // Reset to original placeholder
    this.element.placeholder = this.originalPlaceholder;
    this.currentCharIndex = 0;
    this.isDeleting = false;
  }
  
  typePhrase() {
    if (!this.isTyping) return;
    
    const currentPhrase = this.phrases[this.currentPhraseIndex];
    
    if (!this.isDeleting) {
      // Typing
      this.currentCharIndex++;
      this.element.placeholder = currentPhrase.substring(0, this.currentCharIndex);
      
      if (this.currentCharIndex === currentPhrase.length) {
        // Finished typing, pause then start deleting
        this.timeoutId = setTimeout(() => {
          this.isDeleting = true;
          this.typePhrase();
        }, this.pauseDuration);
      } else {
        // Continue typing
        this.timeoutId = setTimeout(() => this.typePhrase(), this.typeSpeed);
      }
    } else {
      // Deleting
      this.currentCharIndex--;
      this.element.placeholder = currentPhrase.substring(0, this.currentCharIndex);
      
      if (this.currentCharIndex === 0) {
        // Finished deleting, move to next phrase
        this.isDeleting = false;
        this.currentPhraseIndex = (this.currentPhraseIndex + 1) % this.phrases.length;
        this.timeoutId = setTimeout(() => this.typePhrase(), this.typeSpeed);
      } else {
        // Continue deleting
        this.timeoutId = setTimeout(() => this.typePhrase(), this.deleteSpeed);
      }
    }
  }
  
  handleFocus() {
    // Stop typing effect when focused
    this.stopTyping();
  }
  
  handleBlur() {
    // Restart typing effect when blurred if input is empty
    if (!this.element.value) {
      setTimeout(() => this.startTyping(), this.startDelay);
    }
  }
  
  handleInput() {
    // If user clears the input, restart typing effect on blur
    if (!this.element.value && document.activeElement !== this.element) {
      setTimeout(() => this.startTyping(), this.startDelay);
    }
  }
  
  destroy() {
    this.stopTyping();
    this.element.removeEventListener('focus', this.handleFocus);
    this.element.removeEventListener('blur', this.handleBlur);
    this.element.removeEventListener('input', this.handleInput);
    this.element.placeholder = this.originalPlaceholder;
  }
  
  updatePhrases(newPhrases) {
    this.phrases = newPhrases;
    this.currentPhraseIndex = 0;
    this.currentCharIndex = 0;
    this.isDeleting = false;
  }
}

// Enhanced SearchForm to include typing effect
class SearchFormWithTyping extends SearchForm {
  constructor() {
    super();
    this.initTypingEffect();
  }
  
  initTypingEffect() {
    if (!this.input) return;
    
    // Get typing phrases from data attribute
    const phrasesData = this.dataset.typingPhrases;
    let phrases = ['100% whey protein']; // Default phrase
    
    if (phrasesData) {
      try {
        phrases = JSON.parse(phrasesData);
      } catch (e) {
        console.warn('Invalid typing phrases data:', e);
      }
    }
    
    // Initialize typing effect
    this.typingEffect = new SearchTypingEffect(this.input, {
      phrases: phrases,
      typeSpeed: parseInt(this.dataset.typeSpeed) || 50,
      deleteSpeed: parseInt(this.dataset.deleteSpeed) || 30,
      pauseDuration: parseInt(this.dataset.pauseDuration) || 2000,
      startDelay: parseInt(this.dataset.startDelay) || 500
    });
  }
  
  disconnectedCallback() {
    if (this.typingEffect) {
      this.typingEffect.destroy();
    }
  }
}

// Register the enhanced search form
if (!customElements.get('search-form-typing')) {
  customElements.define('search-form-typing', SearchFormWithTyping);
}